import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrFilter } from '@jsr/nostrify__types';
import { Deck, DECK_KIND, isDeckEvent, transformDeckEvent } from '@/lib/nostr/deck';
import { useCurrentUser } from './useCurrentUser';

export interface BatchedDeckResult {
  latestDecks: Deck[];
  followingDecks: Deck[];
  myLists: Deck[];
}

export const useBatchedDecks = () => {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery<BatchedDeckResult>({
    queryKey: ['batched-decks', user?.pubkey],
    queryFn: async ({ signal }) => {
      if (!user) {
        // Non-logged in users: just get latest decks
        const latestDecks = await nostr.query([{
          kinds: [DECK_KIND],
          limit: 10,
        }], { signal });

        const validDecks = latestDecks
          .filter(isDeckEvent)
          .map(transformDeckEvent)
          .filter(deck => {
            const isUnlisted = deck.event.tags.some(t => t[0] === 't' && t[1] === 'unlisted');
            return !isUnlisted;
          })
          .sort((a, b) => b.event.created_at - a.event.created_at)
          .slice(0, 3);

        return {
          latestDecks: validDecks,
          followingDecks: [],
          myLists: [],
        };
      }

      // Step 1: Get contact list first
      const [followingEvent] = await nostr.query([{
        kinds: [3],
        authors: [user.pubkey],
        limit: 1,
      }], { signal });

      const followingPubkeys = followingEvent?.tags
        .filter(tag => tag[0] === 'p')
        .map(tag => tag[1]) || [];

      // Step 2: Build optimized batch query
      const queries: NostrFilter[] = [];

      // Latest decks from everyone (excluding user's own and following)
      queries.push({
        kinds: [DECK_KIND],
        limit: 20,
      });

      // User's own decks
      queries.push({
        kinds: [DECK_KIND],
        authors: [user.pubkey],
        limit: 10,
      });

      // Following decks (only if we have follows)
      if (followingPubkeys.length > 0) {
        queries.push({
          kinds: [DECK_KIND],
          authors: followingPubkeys,
          limit: 15,
        });
      }

      // Execute batched query
      const allEvents = await nostr.query(queries, { signal });

      // Process all deck events
      const allDecks = allEvents.filter(e => e.kind === DECK_KIND);
      
      const validDecks = allDecks
        .filter(isDeckEvent)
        .map(transformDeckEvent)
        .filter(deck => {
          const isUnlisted = deck.event.tags.some(t => t[0] === 't' && t[1] === 'unlisted');
          const isOwnDeck = deck.event.pubkey === user.pubkey;
          return !isUnlisted || isOwnDeck;
        })
        .sort((a, b) => b.event.created_at - a.event.created_at);

      // Categorize based on pubkey
      const result = {
        latestDecks: [] as Deck[],
        followingDecks: [] as Deck[],
        myLists: [] as Deck[],
      };

      validDecks.forEach(deck => {
        const isOwnDeck = deck.event.pubkey === user.pubkey;
        const isFollowing = followingPubkeys.includes(deck.event.pubkey);

        if (isOwnDeck) {
          result.myLists.push(deck);
        } else if (isFollowing) {
          result.followingDecks.push(deck);
        } else {
          result.latestDecks.push(deck);
        }
      });

      // Limit results
      return {
        latestDecks: result.latestDecks.slice(0, 3),
        followingDecks: result.followingDecks.slice(0, 3),
        myLists: result.myLists.slice(0, 3),
      };
    },
  });
};

export const useLatestDecks = () => {
  const { data, isLoading } = useBatchedDecks();
  return { data: data?.latestDecks || [], isLoading };
};

export const useFollowingDecks = () => {
  const { data, isLoading } = useBatchedDecks();
  return { data: data?.followingDecks || [], isLoading };
};

export const useMyLists = () => {
  const { data, isLoading } = useBatchedDecks();
  return { data: data?.myLists || [], isLoading };
};